import {
  PortalBrandingResponseSchema,
  PortalClientHistoryPeriodResponseSchema,
  PortalConfirmUploadResponseSchema,
  PortalMintUploadResponseSchema,
  PortalLogoutResponseSchema,
  PortalLinkAnswerBodySchema,
  PortalLinkConfirmUploadResponseSchema,
  PortalLinkMintUploadBodySchema,
  PortalLinkViewResponseSchema,
  PortalRequestCodeResponseSchema,
  PortalRequestDoneBodySchema,
  PortalRequestItemAnswerBodySchema,
  PortalRequestItemDraftResponseSchema,
  PortalRequestItemDraftSchema,
  PortalRequestResponseSchema,
  PortalSessionResponseSchema,
  PortalSigninLinkReissueResponseSchema,
  PortalThreadListResponseSchema,
  PortalThreadResponseSchema,
  PortalTimelineResponseSchema,
  PortalDeliverablesResponseSchema,
  PortalForwardingResponseSchema,
  PortalFileAccessResponseSchema,
  ApiPortalFileAccessResponseSchema,
  ApiEngagementActionResponseBodySchema,
  EngagementDeliveryListSchema,
  PortalSignatureConsentBodySchema,
  PortalSignatureResponseSchema,
  PortalSignBodySchema,
  PortalSignResponseSchema,
  signatureConsentVersion,
  type PortalAnswerValue,
  type PortalLinkMintUploadBody,
  type PortalLinkUploader,
  type PortalLinkViewResponse,
  type PortalMintUploadBody,
  type PortalRequestItemDraftBody,
  type PortalSessionResponse,
  type PortalSigninLinkBody,
  type PortalSignBody,
} from "@ledgerbox/domain"
import { z } from "zod"

import { readLinkValue } from "./link"
import { putFile } from "./upload"

type Schema<T> = z.ZodType<T>

const ErrorEnvelopeSchema = z.object({
  error: z.looseObject({
    code: z.string().optional(),
    message: z.string().optional(),
  }),
})

export class PortalApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    /** The whole error object, for a route that says more than a sentence
        (a sign-in link names why it failed). */
    readonly details: unknown = null
  ) {
    super(message)
  }
}

/** What a client reads when an action fails. Every rejection from portalApi
 * already carries a sentence a client can act on; anything else falls back to
 * the one wording the portal uses for a failure it cannot name. */
export const errorMessage = (error: Error): string => error.message

const send = async (path: string, init: RequestInit, accept: string) => {
  const headers = new Headers(init.headers)
  headers.set("accept", accept)
  if (init.body) headers.set("content-type", "application/json")
  // Capture the destination when the request begins. A second tab can open a
  // different client link without changing the scope of this in-flight action.
  // The server rejects a protected request without a valid destination.
  const link = readLinkValue()
  if (link) headers.set("x-portal-link", link)
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  })
  if (!response.ok) {
    const error = ErrorEnvelopeSchema.safeParse(await response.json().catch(() => null))
    throw new PortalApiError(
      error.success
        ? (error.data.error.message ?? "Something went wrong. Try again.")
        : "Something went wrong. Try again.",
      response.status,
      error.success
        ? (error.data.error.code ?? "portal_request_failed")
        : "portal_request_failed",
      error.success ? error.data.error : null
    )
  }
  return response
}

const request = async <T>(
  path: string,
  schema: Schema<T>,
  init: RequestInit = {}
): Promise<T> => {
  const response = await send(path, init, "application/json")
  return schema.parse(await response.json().catch(() => null))
}

/** A file the API streams, such as a PDF to sign. */
const requestBytes = async (path: string): Promise<Uint8Array> =>
  new Uint8Array(await (await send(path, {}, "application/pdf")).arrayBuffer())

export const portalApi = {
  /* The upload transport sits on the api object rather than beside it, because
     it is the one call that leaves for storage instead of the API. A caller
     that swaps portalApi swaps the whole conversation, bytes included. */
  putFile,

  getBranding: () =>
    request("/portal/v1/branding", PortalBrandingResponseSchema),

  getSession: (): Promise<PortalSessionResponse> =>
    request("/portal/v1/session", PortalSessionResponseSchema),

  /* The client's forward-to address and any pending Gmail confirmation. Read
     each time the help sheet opens, so a code that arrived since shows up. */
  getForwarding: () =>
    request("/portal/v1/forwarding", PortalForwardingResponseSchema),

  respondEngagementAction: (requestId: string, itemId: string, input: {
    expectedVersion: number
    provider?: "quickbooks" | "xero" | "other" | "none"
    reportedComplete: boolean
  }) => request(
    `/portal/v1/engagement-requests/${requestId}/items/${itemId}/action`,
    z.unknown(),
    { method: "POST", body: JSON.stringify(ApiEngagementActionResponseBodySchema.parse({
      idempotencyKey: crypto.randomUUID(), expectedVersion: input.expectedVersion,
      response: { provider: input.provider, reportedComplete: input.reportedComplete },
    })) }
  ),

  requestEngagementHelp: (requestId: string, itemId: string, input: {
    expectedVersion: number
    kind: "help" | "waiver"
    reason: string
  }) => request(`/portal/v1/engagement-requests/${requestId}/items/${itemId}/help`, z.unknown(), {
    method: "POST", body: JSON.stringify({ idempotencyKey: crypto.randomUUID(), ...input }),
  }),

  reopenEngagementItem: (requestId: string, itemId: string, input: {
    expectedVersion: number
    reason: string
  }) => request(`/portal/v1/engagement-requests/${requestId}/items/${itemId}/reopen`, z.unknown(), {
    method: "POST", body: JSON.stringify({ idempotencyKey: crypto.randomUUID(), ...input }),
  }),

  submitEngagementRequest: (requestId: string) => request(
    `/portal/v1/engagement-requests/${requestId}/submit`, z.unknown(),
    { method: "POST", body: JSON.stringify({ idempotencyKey: crypto.randomUUID() }) }
  ),

  requestCode: (input: { linkToken: string; email: string }) =>
    request("/portal/v1/auth/request-code", PortalRequestCodeResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  verifyCode: (input: {
    linkToken: string
    challengeId: string
    email: string
    code: string
  }) =>
    request("/portal/v1/auth/verify-code", PortalSessionResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /* The link from a request or reminder email. */
  exchangeSigninLink: (input: PortalSigninLinkBody) =>
    request("/portal/v1/auth/link", PortalSessionResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  reissueSigninLink: (input: PortalSigninLinkBody) =>
    request(
      "/portal/v1/auth/link/reissue",
      PortalSigninLinkReissueResponseSchema,
      { method: "POST", body: JSON.stringify(input) }
    ),

  requestSigninLinkCode: (input: PortalSigninLinkBody) =>
    request("/portal/v1/auth/link/code", PortalRequestCodeResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  verifySigninLinkCode: (
    input: PortalSigninLinkBody & { challengeId: string; code: string }
  ) =>
    request("/portal/v1/auth/link/verify-code", PortalSessionResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  logout: () =>
    request("/portal/v1/auth/logout", PortalLogoutResponseSchema, {
      method: "POST",
    }),

  mintUpload: (
    body: PortalMintUploadBody,
    target?: { requestId: string; itemId: string }
  ) =>
    request(
      target
        ? `/portal/v1/requests/${target.requestId}/items/${target.itemId}/uploads`
        : "/portal/v1/uploads",
      PortalMintUploadResponseSchema,
      {
        method: "POST",
        body: JSON.stringify(body),
      }
    ),

  /** `categoryId` only on a question about transactions; the note, which may
      be empty, is then the value. */
  answerItem: (
    itemId: string,
    value: PortalAnswerValue | null,
    categoryId?: string | null
  ) =>
    request(`/portal/v1/items/${itemId}/answer`, PortalRequestResponseSchema, {
      method: "POST",
      body: JSON.stringify(
        PortalRequestItemAnswerBodySchema.parse({ value, categoryId })
      ),
    }),

  saveItemDraft: (itemId: string, draft: PortalRequestItemDraftBody) =>
    request(
      `/portal/v1/items/${itemId}/draft`,
      PortalRequestItemDraftResponseSchema,
      {
        method: "PUT",
        body: JSON.stringify(PortalRequestItemDraftSchema.parse(draft)),
      }
    ),

  confirmUpload: (uploadToken: string) =>
    request("/portal/v1/uploads/confirm", PortalConfirmUploadResponseSchema, {
      method: "POST",
      body: JSON.stringify({ uploadToken }),
    }),

  markRequestDone: (requestId: string, note?: string) =>
    request(
      `/portal/v1/requests/${requestId}/done`,
      PortalSessionResponseSchema,
      {
        method: "POST",
        body: JSON.stringify(PortalRequestDoneBodySchema.parse({ note })),
      }
    ),

  /* Take back "I've sent everything" while the firm has not closed it. */
  undoRequestDone: (requestId: string) =>
    request(
      `/portal/v1/requests/${requestId}/done`,
      PortalSessionResponseSchema,
      { method: "DELETE" }
    ),

  /* Upload by link. With only the client link, these are the calls that
     work: see the open items, send a file, answer an item. */
  openLink: (): Promise<PortalLinkViewResponse> =>
    request("/portal/v1/link/open", PortalLinkViewResponseSchema, {
      method: "POST",
    }),

  mintLinkUpload: (
    body: PortalLinkMintUploadBody,
    target?: { requestId: string; itemId: string }
  ) =>
    request(
      target
        ? `/portal/v1/link/requests/${target.requestId}/items/${target.itemId}/uploads`
        : "/portal/v1/link/uploads",
      PortalMintUploadResponseSchema,
      {
        method: "POST",
        body: JSON.stringify(PortalLinkMintUploadBodySchema.parse(body)),
      }
    ),

  confirmLinkUpload: (uploadToken: string) =>
    request(
      "/portal/v1/link/uploads/confirm",
      PortalLinkConfirmUploadResponseSchema,
      { method: "POST", body: JSON.stringify({ uploadToken }) }
    ),

  answerLinkItem: (
    itemId: string,
    value: PortalAnswerValue,
    uploader?: PortalLinkUploader
  ): Promise<PortalLinkViewResponse> =>
    request(
      `/portal/v1/link/items/${itemId}/answer`,
      PortalLinkViewResponseSchema,
      {
        method: "POST",
        body: JSON.stringify(
          PortalLinkAnswerBodySchema.parse({ value, uploader })
        ),
      }
    ),

  getHistoryPeriod: (periodKey: string) =>
    request(
      `/portal/v1/history/${encodeURIComponent(periodKey)}`,
      PortalClientHistoryPeriodResponseSchema
    ),

  deleteFile: (fileId: string) =>
    request(`/portal/v1/files/${fileId}`, PortalSessionResponseSchema, {
      method: "DELETE",
    }),

  getTimeline: (cursor?: string) =>
    request(
      `/portal/v1/timeline${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
      PortalTimelineResponseSchema
    ),

  listThreads: () =>
    request("/portal/v1/threads", PortalThreadListResponseSchema),

  getThread: (threadId: string) =>
    request(`/portal/v1/threads/${threadId}`, PortalThreadResponseSchema),

  createThread: (input: {
    subject: string
    body: string
    requestId?: string | null
    requestItemId?: string | null
    portalFileId?: string | null
    attachmentIds?: string[]
  }) =>
    request("/portal/v1/threads", PortalThreadResponseSchema, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  postThreadMessage: (
    threadId: string,
    body: string,
    attachmentIds: string[] = []
  ) =>
    request(
      `/portal/v1/threads/${threadId}/messages`,
      PortalThreadResponseSchema,
      { method: "POST", body: JSON.stringify({ body, attachmentIds }) }
    ),

  listDeliverables: () =>
    request("/portal/v1/shares", PortalDeliverablesResponseSchema),

  listEngagementDeliveries: () =>
    request("/portal/v1/engagement-deliveries", EngagementDeliveryListSchema),

  acknowledgeEngagementDelivery: (deliveryId: string) =>
    request(`/portal/v1/engagement-deliveries/${deliveryId}/acknowledge`, EngagementDeliveryListSchema,
      { method: "POST" }),

  getEngagementDeliveryAccess: (deliveryId: string, fileId: string) =>
    request(`/portal/v1/engagement-deliveries/${deliveryId}/files/${fileId}/access`, ApiPortalFileAccessResponseSchema),

  acknowledgeDeliverable: (fileId: string) =>
    request(
      `/portal/v1/shares/${fileId}/acknowledge`,
      PortalDeliverablesResponseSchema,
      { method: "POST" }
    ),

  getItemExample: (itemId: string) =>
    request(
      `/portal/v1/items/${itemId}/example`,
      PortalFileAccessResponseSchema
    ),

  getDeliverableAccess: (fileId: string) =>
    request(
      `/portal/v1/shares/${fileId}/access`,
      PortalFileAccessResponseSchema
    ),

  getSignature: (signatureId: string) =>
    request(`/portal/v1/signatures/${signatureId}`, PortalSignatureResponseSchema),

  getSignatureOriginal: (signatureId: string) =>
    requestBytes(`/portal/v1/signatures/${signatureId}/original`),

  consentToSign: (signatureId: string) =>
    request(`/portal/v1/signatures/${signatureId}/consent`, PortalSignatureResponseSchema, {
      method: "POST",
      body: JSON.stringify(
        PortalSignatureConsentBodySchema.parse({ consentVersion: signatureConsentVersion })
      ),
    }),

  sign: (signatureId: string, body: PortalSignBody) =>
    request(`/portal/v1/signatures/${signatureId}/sign`, PortalSignResponseSchema, {
      method: "POST",
      body: JSON.stringify(PortalSignBodySchema.parse(body)),
    }),
}

/** Everything the portal asks of the outside world. The live object above is
 * one implementation; the demo in ./demo is the other. */
export type PortalApi = typeof portalApi
